'use client';

/**
 * Credits 积分状态管理
 * 全局管理用户积分状态，支持跨组件刷新
 */
import { create } from 'zustand';
import type { CurrentSubscription, CurrentSubscriptionResponse } from '@/lib/api/subscriptions';
import { getAuthSessionVersion, useAuthStore } from '@/stores/authStore';
import { getAuthScopeRevision, registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';
import type { CreditUnitMetadata } from '@/lib/credits/unit';
import { getUserFacingErrorMessage } from '@/lib/errors/user-facing-error';
import { translateClient } from '@/lib/i18n/client';

export interface UserCredits {
  wallet_frozen?: boolean;
  credits_balance: number;           // 当前可用积分 (subscription + purchased)
  free_credit_policy?: 'daily' | 'welcome_once';
  welcome_credit_amount?: number;
  welcome_credit_status?: 'pending' | 'granted' | 'legacy_satisfied' | 'ineligible' | null;
  welcome_credit_granted_at?: string | null;
  subscription_credits_remaining: number; // 订阅当期积分
  subscription_credits_expires_at?: string | null; // 订阅积分到期时间 (UTC)
  purchased_credits: number;         // 永久余额；后付费训练可为负数，充值自然抵扣
  tier: string;                      // 会员等级
  credits_total_granted?: number;    // 累计获得
  credits_total_consumed?: number;   // 累计消耗
  credit_unit_mode?: 'dobi' | string;
  unit_key?: string;
  unit_label_zh?: string;
  unit_label_en?: string;
  scale?: number;
  credits_per_image?: number;
  credit_unit?: CreditUnitMetadata | null;
}

interface CreditsStore {
  credits: UserCredits | null;
  subscription: CurrentSubscription | null;
  /** 首次加载或用户主动刷新；刷新时不会清空已知快照。 */
  loading: boolean;
  /** 已有余额时正在更新，不论前台或静默请求。 */
  refreshing: boolean;
  error: string | null;
  updatedAt: number | null;
  initialized: boolean;
  initCredits: () => Promise<void>;
  refetchCredits: (options?: { silent?: boolean }) => Promise<void>;
  setCredits: (credits: UserCredits | null) => void;
  clearCredits: () => void;
}

const ACCOUNT_REQUEST_TIMEOUT_MS = 10_000;
let creditsRequestVersion = 0;
let pendingRequest: { promise: Promise<void>; cancel: () => void } | null = null;
let snapshotOwner: string | null = null;

function currentOwner(): string | null {
  const auth = useAuthStore.getState();
  return auth.isAuthenticated && auth.user?.id
    ? `${getAuthSessionVersion()}:${getAuthScopeRevision()}:${auth.user.id}`
    : null;
}

function isSignedCredit(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isFiniteCredit(value: unknown): value is number {
  return isSignedCredit(value) && value >= 0;
}

function isUserCreditsPayload(value: unknown): value is UserCredits {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return ['credits_balance', 'purchased_credits'].every(key => isSignedCredit(record[key]))
    && isFiniteCredit(record.subscription_credits_remaining)
    && ['credits_total_granted', 'credits_total_consumed'].every(key =>
      record[key] === undefined || isFiniteCredit(record[key]))
    && typeof record.tier === 'string' && Boolean(record.tier.trim());
}

/** 套餐和余额必须来自同一次响应，缺失额度不能伪装成免费套餐或满额。 */
function readAccountSnapshot(data: CurrentSubscriptionResponse | undefined): {
  credits: UserCredits; subscription: CurrentSubscription;
} {
  const subscription = data?.subscription;
  const wallet = data?.credits;
  const plan = subscription?.plan;
  if (!data || data.success === false || !wallet || !subscription || !plan
    || typeof plan.slug !== 'string' || !plan.slug.trim()
    || typeof plan.name !== 'string' || !plan.name.trim()
    || !isFiniteCredit(plan.credits_per_month)) {
    throw new Error(translateClient('errors', 'common.fallbackMessage'));
  }
  const credits: UserCredits = {
    credits_balance: wallet.balance,
    wallet_frozen: wallet.wallet_frozen === true,
    free_credit_policy: wallet.free_credit_policy,
    welcome_credit_amount: wallet.welcome_credit_amount,
    welcome_credit_status: wallet.welcome_credit_status,
    welcome_credit_granted_at: wallet.welcome_credit_granted_at,
    subscription_credits_remaining: wallet.subscription_credits_remaining,
    subscription_credits_expires_at: wallet.subscription_credits_expires_at ?? null,
    purchased_credits: wallet.purchased_credits,
    tier: plan.slug,
    credits_total_granted: wallet.total_granted,
    credits_total_consumed: wallet.total_consumed,
    credit_unit_mode: wallet.credit_unit_mode,
    unit_key: wallet.unit_key,
    unit_label_zh: wallet.unit_label_zh,
    unit_label_en: wallet.unit_label_en,
    scale: wallet.scale,
    credits_per_image: wallet.credits_per_image,
    credit_unit: wallet.credit_unit,
  };
  if (!isUserCreditsPayload(credits)) {
    throw new Error(translateClient('errors', 'common.fallbackMessage'));
  }
  return { credits, subscription };
}

/** 失效与取消必须同时发生；即便 fetch 忽略 abort，旧响应也不能再写入。 */
function invalidateRequest(): void {
  creditsRequestVersion += 1;
  pendingRequest?.cancel();
  pendingRequest = null;
}

function requestAccountSnapshot(silent: boolean): Promise<void> {
  const owner = currentOwner();
  if (!owner) {
    useCreditsStore.getState().clearCredits();
    return Promise.resolve();
  }
  if (snapshotOwner !== owner) {
    useCreditsStore.getState().clearCredits();
    snapshotOwner = owner;
  }
  // 轮询和首屏初始化共用在途请求；操作后的强制刷新必须发出新的读请求。
  if (silent && pendingRequest) return pendingRequest.promise;
  invalidateRequest();
  const version = creditsRequestVersion;
  const controller = new AbortController();
  const hasSnapshot = useCreditsStore.getState().credits !== null;
  useCreditsStore.setState({
    initialized: true,
    loading: !silent || !hasSnapshot,
    refreshing: hasSnapshot,
    // error 保留到成功响应，避免重试期间冒充最新余额。
  });
  const isCurrent = () => version === creditsRequestVersion && currentOwner() === owner;
  let timer: ReturnType<typeof setTimeout>;
  // deadline 覆盖模块加载、认证续期、fetch 和 JSON 解析；仅 AbortSignal 不足以约束认证等待。
  const interrupted = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(new Error('ACCOUNT_REQUEST_ABORTED')), { once: true });
    timer = setTimeout(() => {
      reject(new Error(translateClient('errors', 'common.fallbackMessage')));
      controller.abort();
    }, ACCOUNT_REQUEST_TIMEOUT_MS);
  });
  const promise = (async () => {
    try {
      const response = await Promise.race([
        // ApiClient 的诊断链路会加载业务 store；在调用边界加载，避免 SSR 模块初始化循环。
        import('@/lib/api/subscriptions').then(({ subscriptionsApi }) => {
          if (!isCurrent() || controller.signal.aborted) throw new Error('ACCOUNT_REQUEST_ABORTED');
          return subscriptionsApi.getCurrent({ includePurchaseHistory: false, signal: controller.signal });
        }),
        interrupted,
      ]);
      if (!isCurrent()) return;
      if (response.success !== true || response.error) {
        throw response.error ?? new Error(translateClient('errors', 'common.fallbackMessage'));
      }
      const snapshot = readAccountSnapshot(response.data);
      useCreditsStore.setState({ ...snapshot, loading: false, refreshing: false, error: null, updatedAt: Date.now() });
    } catch (err) {
      if (!isCurrent()) return;
      useCreditsStore.setState({
        error: getUserFacingErrorMessage(err, 'credits', translateClient('errors', 'common.fallbackMessage')),
        loading: false,
        refreshing: false,
      });
    } finally {
      clearTimeout(timer!);
      if (version === creditsRequestVersion) {
        pendingRequest = null;
        // 认证变化可发生在重置通知之前，不能留下旧账户或永久 loading。
        if (currentOwner() !== owner) useCreditsStore.getState().clearCredits();
      }
    }
  })();
  pendingRequest = { promise, cancel: () => controller.abort() };
  return promise;
}

export const useCreditsStore = create<CreditsStore>((set, get) => ({
  credits: null,
  subscription: null,
  loading: true,
  refreshing: false,
  error: null,
  updatedAt: null,
  initialized: false,

  initCredits: async () => {
    if (snapshotOwner === currentOwner() && get().credits && get().subscription) return;
    await requestAccountSnapshot(true);
  },

  refetchCredits: async (options) => {
    await requestAccountSnapshot(options?.silent === true);
  },

  // 兼容直接余额写入，但不能让此前的在途读取覆盖新值，也不猜测套餐额度。
  setCredits: (credits) => {
    if (credits === null) { get().clearCredits(); return; }
    if (!isUserCreditsPayload(credits)) return;
    const owner = currentOwner();
    if (!owner) { get().clearCredits(); return; }
    invalidateRequest();
    const subscription = snapshotOwner === owner && get().subscription?.plan?.slug === credits.tier
      ? get().subscription : null;
    snapshotOwner = owner;
    set({ credits, subscription, loading: false, refreshing: false, error: null, initialized: true, updatedAt: null });
  },

  clearCredits: () => {
    invalidateRequest();
    snapshotOwner = null;
    set({ credits: null, subscription: null, loading: false, refreshing: false, error: null, updatedAt: null, initialized: false });
  },
}));

// 退出/切换账户时同步清除，不能依赖稍后动态 import 的重置结果。
registerAuthScopedCacheReset('credits-account-snapshot', () => useCreditsStore.getState().clearCredits());
